import type { To } from 'react-router';

import type { PortProps } from '../Port/Port';
import type { HardwareSlotsLayout } from '../Slots/SlotsRendered';
import { Alert } from '../../components/Alert/Alert';
import { IconTooltip } from '../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Device } from '../Device/Device';
import { HardwareSlotsLayoutRenderer } from '../Slots/SlotsRendered';
import { HardwareDeviceModel, isGen1Hardware } from '../utils';
import {
  FirewallLayoutsF1,
  FirewallLayoutsF2,
  FirewallLayoutsF3Primary,
  FirewallLayoutsF3Secondary,
  FirewallLayoutsMC05,
  FirewallLayoutsMC11,
} from './FirewallLayouts/FirewallLayouts';

const SecurityApplianceAntennaLEDRepresentation = styled('div', {
  display: 'flex',
  width: '$16',
  height: '$12',
  backgroundColor: colors.bgPositiveLight,
  strokeAll: colors.strokePositiveLight,
  borderRadius: '$4',

  [darkThemeSelector]: {
    backgroundColor: colors.bgPositiveDark,
    strokeAll: colors.strokePositiveDark,
  },
});
SecurityApplianceAntennaLEDRepresentation.displayName = 'SecurityApplianceAntennaLEDRepresentation';

const SecurityApplianceAntennaLEDContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
SecurityApplianceAntennaLEDContainer.displayName = 'SecurityApplianceAntennaLEDContainer';

function SecurityApplianceAntennaLED({ children }: { children: string }) {
  return (
    <SecurityApplianceAntennaLEDContainer>
      <SecurityApplianceAntennaLEDRepresentation />
      <Small weight="bold">{children}</Small>
    </SecurityApplianceAntennaLEDContainer>
  );
}

const SecurityApplianceAntennaLEDs = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
SecurityApplianceAntennaLEDs.displayName = 'SecurityApplianceAntennaLEDs';

const SecurityApplianceAntennaTooltip = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$10',
  widthAll: '$200',
  padding: '$8',
});
SecurityApplianceAntennaTooltip.displayName = 'SecurityApplianceAntennaTooltip';

function SecurityApplianceAntenna() {
  return (
    <IconTooltip
      tight
      side="right"
      icon="antenna"
      contents={
        <SecurityApplianceAntennaTooltip>
          <Alert
            type="inline"
            icon="information"
            variant="neutral"
            copy="Illustrative purposes only, doesn't reflect actual device status."
          />
          <SecurityApplianceAntennaLEDs>
            <Small weight="bold">Antenna LED layout</Small>
            <SecurityApplianceAntennaLED>Backend</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>LTE</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>WAN</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>Power</SecurityApplianceAntennaLED>
          </SecurityApplianceAntennaLEDs>
        </SecurityApplianceAntennaTooltip>
      }
    />
  );
}

const FirewallMetadata = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});
FirewallMetadata.displayName = 'FirewallMetadata';

export type FirewallModel =
  | HardwareDeviceModel.Mc01
  | HardwareDeviceModel.Mc05
  | HardwareDeviceModel.Mc06
  | HardwareDeviceModel.Mc11
  | HardwareDeviceModel.F1
  | HardwareDeviceModel.F2
  | HardwareDeviceModel.F3;

// Type guard to check if a DeviceModel is a valid FirewallModel
export function isFirewallModel(deviceModel: string): deviceModel is FirewallModel {
  return [
    HardwareDeviceModel.Mc01,
    HardwareDeviceModel.Mc05,
    HardwareDeviceModel.Mc06,
    HardwareDeviceModel.Mc11,
    HardwareDeviceModel.F1,
    HardwareDeviceModel.F2,
    HardwareDeviceModel.F3,
  ].includes(deviceModel as FirewallModel);
}

// Safe conversion function from DeviceModel to FirewallModel
export function toFirewallModel(deviceModel: string): FirewallModel | null {
  return isFirewallModel(deviceModel) ? deviceModel : null;
}

type FirewallProps = {
  labels: {
    primary: { as?: React.ElementType; to?: To; content: string };
    secondary?: { as?: React.ElementType; to?: To; content: string };
  };
  model: FirewallModel;
  ports: PortProps[];
};

function getFirewallLayout(model: FirewallModel, isPrimary: boolean): HardwareSlotsLayout {
  switch (model) {
    case HardwareDeviceModel.Mc05:
    case HardwareDeviceModel.Mc06:
      // MC05 and MC06 have the  same port layout
      return FirewallLayoutsMC05;
    case HardwareDeviceModel.Mc11:
      return FirewallLayoutsMC11;
    case HardwareDeviceModel.F1:
      return FirewallLayoutsF1;
    case HardwareDeviceModel.F2:
      return FirewallLayoutsF2;
    case HardwareDeviceModel.F3:
      return isPrimary ? FirewallLayoutsF3Primary : FirewallLayoutsF3Secondary;
    default:
      return FirewallLayoutsMC05;
  }
}

export function getFirewallPortOrientation(
  model: FirewallModel,
  portNumber: number,
): 'up' | 'down' | undefined {
  const layout = getFirewallLayout(model, true);

  for (const column of layout.columns) {
    for (const row of column.rows) {
      if (row.ports.includes(portNumber)) {
        return row.portOrientation;
      }
    }
  }

  return undefined;
}

export function Firewall({ labels: { primary, secondary }, model, ports }: FirewallProps) {
  const gen1Hardware = isGen1Hardware(model);
  const isDualDevice = model === HardwareDeviceModel.F3;

  return (
    <Device
      slots={
        isDualDevice
          ? [
              {
                id: 'primary',
                content: (
                  <HardwareSlotsLayoutRenderer
                    model={model}
                    label={{ content: primary.content, as: primary.as, to: primary.to }}
                    layout={getFirewallLayout(model, true)}
                    ports={ports}
                  />
                ),
              },
              {
                id: 'secondary',
                content: (
                  <HardwareSlotsLayoutRenderer
                    model={model}
                    label={{
                      content: secondary?.content || 'Secondary',
                      as: secondary?.as,
                      to: secondary?.to,
                    }}
                    layout={getFirewallLayout(model, false)}
                    ports={ports}
                  />
                ),
              },
            ]
          : [
              {
                id: 'primary',
                content: (
                  <HardwareSlotsLayoutRenderer
                    model={model}
                    label={{ content: primary.content, as: primary.as, to: primary.to }}
                    layout={getFirewallLayout(model, true)}
                    ports={ports}
                  />
                ),
              },
            ]
      }
      metadata={
        !gen1Hardware && (
          <FirewallMetadata>
            <SecurityApplianceAntenna />
          </FirewallMetadata>
        )
      }
    />
  );
}
